/**
 * Custom HTTP Client for react-admin dataProvider
 * Adds Authorization header with JWT token to all requests
 * Handles token expiration and 401 responses
 */
import { fetchUtils } from 'react-admin';
import { isTokenExpired } from './jwtUtils';

/**
 * Custom HTTP client that:
 * 1. Adds Authorization header with JWT token
 * 2. Handles 401 responses (expired token) by clearing session
 * 3. Validates token before making requests
 */
const httpClient = (url: string, options: fetchUtils.Options = {}) => {
    // Initialize headers if not present
    if (!options.headers) {
        options.headers = new Headers({ Accept: 'application/json' });
    } else if (!(options.headers instanceof Headers)) {
        options.headers = new Headers(options.headers);
    }

    // Get token from localStorage
    const token = localStorage.getItem('user_authToken');
    
    // Check if token exists and is not expired before adding to header
    if (token && !isTokenExpired(token)) {
        options.headers.set('Authorization', `Bearer ${token}`);
    } else if (token && isTokenExpired(token)) {
        // Token is expired - clear session
        console.log('Token expired in httpClient - clearing session');
        localStorage.clear();
    }

    // Debug logging for customer search requests
    if (url.includes('/customers') && !url.includes('/trends') && !url.includes('/conversion') && !url.includes('/visitor') && !url.includes('/funnels') && !url.includes('/page-views') && !url.includes('/activities') && !url.includes('/session') && !url.includes('/utm')) {
        console.log('🔍 [CLIENT] Customer API Request URL:', url);
        console.log('🔍 [CLIENT] URL includes customer_search?', url.includes('customer_search'));
        console.log('🔍 [CLIENT] URL includes filter?', url.includes('filter='));
        // Extract query params for easier debugging
        try {
            const urlObj = new URL(url);
            console.log('🔍 [CLIENT] Query params:', Object.fromEntries(urlObj.searchParams));
        } catch (e) {
            console.log('🔍 [CLIENT] Could not parse URL:', e);
        }
    }

    // Make the request
    return fetchUtils.fetchJson(url, options).catch((error) => {
        // Handle 401 Unauthorized responses
        if (error.status === 401 || error.status === 403) {
            console.log('Received 401/403 response - token may be expired or invalid');
            localStorage.clear();
            // Re-throw to trigger react-admin's error handling
            throw error;
        }
        // Re-throw other errors
        throw error;
    });
};

export default httpClient;
